import { useEffect, useState } from "react";
import { auth, db } from "../services/firebase";
import { ref, get } from "firebase/database";

/**
 * ✅ Fetches user's purchased packs for a domain.
 * Checks expiry using `expiresAt` and returns a map of purchase info.
 *
 * Example returned object:
 * {
 *   "packId1": { active: true, expiresAt: 1731883300000 },
 *   "packId2": { active: false, expired: true }
 * }
 */
export default function useUserPurchases(domainId) {
    const [purchasedPacks, setPurchasedPacks] = useState({});
    const [loadingPurchases, setLoadingPurchases] = useState(false);

    useEffect(() => {
        const fetchPurchases = async () => {
            const user = auth.currentUser;
            if (!user || !domainId) return;

            setLoadingPurchases(true);
            try {
                const purchaseRef = ref(db, `Payments/Purchases/${user.uid}/${domainId}`);
                const snapshot = await get(purchaseRef);

                if (snapshot.exists()) {
                    const allPurchases = snapshot.val();
                    const now = Date.now();

                    const formatted = Object.entries(allPurchases).reduce((acc, [packId, data]) => {
                        const expiresAt = data.expiresAt || null;
                        const isExpired = expiresAt ? now > expiresAt : false;
                        acc[packId] = {
                            ...data,
                            expired: isExpired,
                            active: !isExpired,
                        };
                        return acc;
                    }, {});

                    setPurchasedPacks(formatted);
                } else {
                    setPurchasedPacks({});
                }
            } catch (error) {
                console.error("❌ Error fetching purchases:", error);
            } finally {
                setLoadingPurchases(false);
            }
        };

        fetchPurchases();
    }, [domainId]);

    return { purchasedPacks, loadingPurchases };
}
